import type { TFunction } from 'i18next';
import { CustomerGroup, CustomerTargetGroup } from '../../../types/data';

import FacebookIcon from '@mui/icons-material/Facebook';
import GoogleIcon from '@mui/icons-material/Google';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

export interface AdDraft {
  title: string;
  content: string;
  date: string;
  charCount: number;
  imageUrl?: string;
  ctaText?: string;
  domain?: string;
  companyName?: string;
}

// Content returned from the API
export interface AdContent {
  title: string;
  content: string;
  image?: string;
  cta?: string;
  domain?: string;
}

// Utility functions
export const extractSubject = (content: string): string | null => {
  const subjectMatch = content.match(/Title:(.+?)(\n|$)/i);
  return subjectMatch ? subjectMatch[1].trim() : null;
};

export const extractImageUrl = (content: string): string | undefined => {
  const imageMatch = content.match(/ImageUrl:(.+?)(\n|$)/i);
  return imageMatch ? imageMatch[1].trim() : undefined;
};

// Convert CustomerTargetGroup to CustomerGroup
export const mapToCustomerGroups = (targetGroups: CustomerTargetGroup[]): CustomerGroup[] => {
  return targetGroups.map((group) => ({
    name: group.name,
    description: group.description || '',
  }));
};

// Format parsed API draft content into AdDraft objects
export const formatDraft = (
  content: string,
  index: number,
  t: TFunction,
  companyName?: string
): AdDraft => {
  try {
    const parsedContent = JSON.parse(content) as AdContent;

    return {
      title: parsedContent.title || `${t('ads.draft')} ${index + 1}`,
      content: content,
      date: new Date().toLocaleString(),
      charCount: content.length,
      imageUrl: parsedContent.image, // Direct image URL from DALL-E
      ctaText: parsedContent.cta,
      domain: parsedContent.domain,
      companyName: companyName,
    };
  } catch {
    return {
      title: extractSubject(content) || `${t('ads.draft')} ${index + 1}`,
      content,
      date: new Date().toLocaleString(),
      charCount: content.length,
      imageUrl: extractImageUrl(content),
    };
  }
};

// Define the available ad platforms
export const AD_PLATFORMS = [
  { value: 'facebook_ads', name: 'Meta', icon: FacebookIcon, disabled: false },
  { value: 'google_ads', name: 'Google', icon: GoogleIcon, disabled: false },
  { value: 'linkedin_ads', name: 'LinkedIn', icon: LinkedInIcon, disabled: false },
];
